import React, { useState, useCallback, useEffect } from 'react';
import { Star, Plus, Search, MoreHorizontal, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { boardColors } from '../mockData';

// Move CreateBoardDialog outside to prevent re-creation
const CreateBoardDialog = ({ 
  isOpen, 
  onOpenChange, 
  boardTitle, 
  onTitleChange, 
  selectedColor, 
  onColorChange, 
  onCreateBoard, 
  isCreating 
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Card className="cursor-pointer hover:shadow-md transition-shadow duration-200 bg-gray-100 border-0">
          <CardContent className="p-4 flex items-center justify-center h-24">
            <div className="text-center text-gray-600">
              <Plus className="h-6 w-6 mx-auto mb-1" />
              <span className="text-sm">Create new board</span>
            </div>
          </CardContent>
        </Card>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create board</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Board title</label>
            <Input
              value={boardTitle}
              onChange={onTitleChange}
              placeholder="Enter board title..."
              className="mt-1"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  onCreateBoard();
                }
              }}
              autoFocus
            />
          </div>
          
          <div>
            <label className="text-sm font-medium mb-2 block">Background</label>
            <div className="grid grid-cols-5 gap-2">
              {boardColors.map((color, index) => (
                <div
                  key={index}
                  className={`w-12 h-8 rounded cursor-pointer border-2 transition-all ${
                    selectedColor === color ? 'border-gray-800 scale-110' : 'border-gray-200 hover:border-gray-400'
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => onColorChange(color)}
                />
              ))}
            </div>
          </div>
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={onCreateBoard}
              disabled={!boardTitle.trim() || isCreating}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isCreating ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const BoardList = ({ boards, onSelectBoard, onCreateBoard, onUpdateBoard, onDeleteBoard, loading, workspace, createBoardTrigger }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [newBoardTitle, setNewBoardTitle] = useState('');
  const [selectedColor, setSelectedColor] = useState(boardColors[0]);
  const [isCreatingBoard, setIsCreatingBoard] = useState(false);

  // Handle create board trigger from header
  useEffect(() => {
    if (createBoardTrigger) {
      setIsCreating(true);
    }
  }, [createBoardTrigger]);

  const filteredBoards = boards.filter(board =>
    board.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const starredBoards = filteredBoards.filter(board => board.starred);
  const otherBoards = filteredBoards.filter(board => !board.starred);

  const toggleStar = async (boardId) => {
    const board = boards.find(b => b.id === boardId);
    if (board) {
      const updatedBoard = { ...board, starred: !board.starred };
      await onUpdateBoard(updatedBoard);
    }
  };

  const [deleteConfirmBoard, setDeleteConfirmBoard] = useState(null);

  const handleDeleteConfirm = async () => {
    if (deleteConfirmBoard) {
      await onDeleteBoard(deleteConfirmBoard.id);
      setDeleteConfirmBoard(null);
    }
  };

  const BoardCard = ({ board }) => (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow duration-200 border-0 group"
      style={{ backgroundColor: board.color }}
    >
      <CardContent className="p-4 relative">
        <div className="flex items-start justify-between">
          <div 
            className="flex-1 cursor-pointer"
            onClick={() => onSelectBoard(board)}
          >
            <h3 className="text-white font-semibold text-lg truncate pr-2">
              {board.title}
            </h3>
            <div className="mt-2 text-white/80 text-sm">
              {board.lists?.length || 0} lists
            </div>
          </div>
          
          <div className="flex items-center space-x-1">
            <Button
              variant="ghost"
              size="sm"
              className="p-1 hover:bg-white/20 text-white"
              onClick={(e) => {
                e.stopPropagation();
                toggleStar(board.id);
              }}
            >
              <Star className={`h-4 w-4 ${board.starred ? 'fill-yellow-400 text-yellow-400' : ''}`} />
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-1 hover:bg-white/20 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleStar(board.id);
                  }}
                >
                  <Star className="h-4 w-4 mr-2" />
                  {board.starred ? 'Remove from favorites' : 'Add to favorites'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteConfirmBoard(board);
                  }}
                  className="text-red-600 focus:text-red-600"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete board
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const handleTitleChange = useCallback((e) => {
    setNewBoardTitle(e.target.value);
  }, []);

  const handleColorChange = useCallback((color) => {
    setSelectedColor(color);
  }, []);

  const handleDialogOpenChange = useCallback((open) => {
    setIsCreating(open);
    if (!open) {
      setNewBoardTitle('');
      setSelectedColor(boardColors[0]);
    }
  }, []);

  const handleCreateBoard = useCallback(async () => {
    if (!newBoardTitle.trim() || isCreatingBoard) return;
    
    try {
      setIsCreatingBoard(true);
      const newBoardData = {
        title: newBoardTitle,
        color: selectedColor
      };
      
      await onCreateBoard(newBoardData);
      handleDialogOpenChange(false);
    } catch (error) {
      console.error('Error creating board:', error);
    } finally {
      setIsCreatingBoard(false);
    }
  }, [newBoardTitle, selectedColor, isCreatingBoard, onCreateBoard, handleDialogOpenChange]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search boards..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white border-gray-200 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Starred Boards */}
        {starredBoards.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <Star className="h-5 w-5 text-yellow-500 mr-2" />
              <h2 className="text-xl font-semibold text-gray-800">Starred Boards</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {starredBoards.map(board => (
                <BoardCard key={board.id} board={board} />
              ))}
            </div>
          </div>
        )}

        {/* All Boards */}
        <div>
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            {starredBoards.length > 0 ? 'Your Boards' : 'Your Boards'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <CreateBoardDialog
              isOpen={isCreating}
              onOpenChange={handleDialogOpenChange}
              boardTitle={newBoardTitle}
              onTitleChange={handleTitleChange}
              selectedColor={selectedColor}
              onColorChange={handleColorChange}
              onCreateBoard={handleCreateBoard}
              isCreating={isCreatingBoard}
            />
            {otherBoards.map(board => (
              <BoardCard key={board.id} board={board} />
            ))}
          </div>
        </div>

        {filteredBoards.length === 0 && searchQuery && (
          <div className="text-center py-12">
            <div className="text-gray-500">
              <Search className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg mb-2">No boards found</p>
              <p className="text-sm">Try adjusting your search query</p>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmBoard} onOpenChange={() => setDeleteConfirmBoard(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Board</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteConfirmBoard?.title}"? This action cannot be undone and will permanently delete the board and all its lists and cards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Delete Board
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BoardList;